import React, { useRef, useEffect } from 'react';
import {
  Rocket, Code, Clock, Users, BarChart, BrainCircuit, LayoutGrid,
  CheckCircle, ArrowRight, Zap, Server, GitBranch, ShieldCheck, Orbit
} from 'lucide-react';
import Button from '@/components/ui/Button';
import { COMPANY_INFO } from '@/lib/constants';
import { motion, useScroll, useTransform } from 'framer-motion';
import FunctionalityCard from '@/components/sections/FunctionalityCard';
import SEO from '@/components/SEO';
import EstimationForm from '@/components/sections/EstimationForm';
import BookingModule from '@/components/sections/BookingModule';
import FAQ from '@/components/ui/FAQ';

const SaasDevelopment = () => {
  const heroRef = useRef<HTMLDivElement>(null);
  const processRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: processRef,
    offset: ["start end", "end start"]
  });

  const processOpacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);

  const benefits = [
    {
      icon: <Clock className="w-full h-full text-orcsys-500" />,
      title: "Développement rapide",
      description: "Nous accélérons le processus de développement grâce à nos méthodologies Agile et nos frameworks optimisés."
    },
    {
      icon: <Rocket className="w-full h-full text-orcsys-500" />,
      title: "Prototypage efficace",
      description: "Validez vos idées rapidement avec nos prototypes fonctionnels et itérez en fonction des retours utilisateurs."
    },
    {
      icon: <Users className="w-full h-full text-orcsys-500" />,
      title: "Approche transparente",
      description: "Communication claire et régulière pour vous tenir informé de l'avancement de votre projet à chaque étape."
    },
    {
      icon: <BrainCircuit className="w-full h-full text-orcsys-500" />,
      title: "Solutions innovantes",
      description: "Nous intégrons les technologies les plus récentes et les meilleures pratiques pour créer des solutions pérennes."
    }
  ];

  const features = [
    {
      icon: <Server className="w-5 h-5 text-orcsys-500" />,
      title: "Architecture scalable",
      description: "Infrastructure cloud évolutive adaptée à votre croissance"
    },
    {
      icon: <ShieldCheck className="w-5 h-5 text-orcsys-500" />,
      title: "Sécurité renforcée",
      description: "Protection des données et conformité aux standards"
    },
    {
      icon: <LayoutGrid className="w-5 h-5 text-orcsys-500" />,
      title: "Interfaces intuitives",
      description: "Expérience utilisateur fluide et ergonomique"
    },
    {
      icon: <GitBranch className="w-5 h-5 text-orcsys-500" />,
      title: "CI/CD automatisés",
      description: "Déploiement continu pour des mises à jour rapides"
    },
    {
      icon: <BarChart className="w-5 h-5 text-orcsys-500" />,
      title: "Analytics intégrés",
      description: "Suivi et analyse des performances en temps réel"
    },
    {
      icon: <Orbit className="w-5 h-5 text-orcsys-500" />,
      title: "APIs extensibles",
      description: "Intégration facile avec vos systèmes existants"
    }
  ];

  const processSteps = [
    {
      number: "01",
      title: "Discovery & Planning",
      description: "Nous analysons vos besoins et définissons ensemble les objectifs et fonctionnalités clés de votre SaaS."
    },
    {
      number: "02",
      title: "Conception & Design",
      description: "Création des wireframes, design système et maquettes UI/UX pour visualiser l'expérience utilisateur."
    },
    {
      number: "03",
      title: "Développement MVP",
      description: "Développement itératif d'un produit minimum viable pour valider rapidement le concept sur le marché."
    },
    {
      number: "04",
      title: "Tests & Optimisation",
      description: "Tests rigoureux pour garantir la qualité, la performance et la sécurité de votre application."
    },
    {
      number: "05",
      title: "Déploiement & Lancement",
      description: "Mise en production et accompagnement lors du lancement commercial de votre solution."
    },
    {
      number: "06",
      title: "Maintenance & Évolution",
      description: "Support continu, monitoring et implémentation de nouvelles fonctionnalités pour faire évoluer votre produit."
    }
  ];

  const faqItems = [
    {
      question: "Qu'est-ce qui caractérise une solution SaaS ?",
      answer: "Une solution SaaS (Software as a Service) est un logiciel hébergé dans le cloud, accessible via un navigateur web. Elle se caractérise par un modèle d'abonnement, une mise à jour continue, une scalabilité automatique, et une accessibilité multiplateforme sans installation locale."
    },
    {
      question: "Comment gérez-vous la scalabilité des applications SaaS ?",
      answer: "Nous concevons l'architecture pour la scalabilité dès le départ : infrastructure cloud élastique, bases de données distribuées, mise en cache intelligente, et microservices. Cela permet d'accompagner votre croissance de quelques utilisateurs à plusieurs milliers sans interruption de service."
    },
    {
      question: "Comment assurez-vous la sécurité des données ?",
      answer: "Nous implémentons une sécurité multicouche : chiffrement des données au repos et en transit, authentification multi-facteurs, isolation des données par tenant, audits réguliers, et conformité RGPD. Nous suivons également les meilleures pratiques OWASP pour la sécurité des applications web."
    },
    {
      question: "Proposez-vous l'intégration avec d'autres services ?",
      answer: "Oui, nous développons des API RESTful et des webhooks pour l'intégration avec d'autres services. Nous pouvons intégrer votre SaaS avec des outils populaires comme Stripe pour les paiements, HubSpot pour le CRM, Slack pour les notifications, etc."
    },
    {
      question: "Comment gérez-vous la facturation et les abonnements ?",
      answer: "Nous implémentons des systèmes de facturation flexibles avec différents modèles d'abonnement, facturation à l'usage, essais gratuits, et upgrades/downgrades automatiques. Nous intégrons des solutions de paiement sécurisées comme Stripe ou PayPal avec gestion automatisée des factures."
    },
    {
      question: "Quel support proposez-vous après le lancement ?",
      answer: "Nous offrons un support complet : monitoring 24/7, mises à jour de sécurité, optimisation des performances, support technique pour vos clients, et évolution fonctionnelle continue. Nous fournissons également des tableaux de bord d'analytics pour suivre l'utilisation."
    }
  ];

  return (
    <>
      <SEO
        title="Développement SaaS | Solutions Cloud Innovantes"
        description="Créez votre solution SaaS avec Orcsys. Développement sur mesure, architecture scalable, et accompagnement expert pour transformer votre idée en produit performant. Expertise en cloud computing et applications web modernes."
        keywords="développement SaaS, cloud computing, logiciel as a service, application web, développement agile, solution entreprise, architecture cloud, scalabilité, microservices, API"
        type="service"
        url="https://innov.orcsys.fr/developpement-saas"
        image="https://innov.orcsys.fr/images/saas-development-preview.jpg"
      />
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section ref={heroRef} className="relative min-h-[90vh] flex items-center justify-center pt-24 pb-16 overflow-hidden">
          {/* Background elements */}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-50 to-white" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(142,227,205,0.3)_0%,rgba(255,255,255,0)_60%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(155,135,245,0.2)_0%,rgba(255,255,255,0)_60%)]" />

          {/* Grid pattern */}
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBzdHJva2U9IiNkZGQiIGZpbGw9Im5vbmUiIGZpbGwtcnVsZT0iZXZlbm9kZCI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMjkiLz48cGF0aCBkPSJNMzAgMUw2MCAzMCAzMCA1OSAxIDMweiIvPjwvZz48L3N2Zz4=')] opacity-[0.03]" />

          <div className="container px-4 mx-auto relative z-10">
            <div className="max-w-5xl mx-auto">
              <motion.div
                className="text-center mb-12"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
                  Transformez vos idées en <span className="text-orcsys-500">SaaS innovants</span> et performants
                </h1>
                <p className="text-lg text-slate-600 max-w-3xl mx-auto mb-8">
                  Nous développons des solutions SaaS sur mesure qui répondent aux besoins de votre entreprise,
                  avec une approche agile et un développement accéléré pour vous donner un avantage concurrentiel.
                </p>

                <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
                  <Button.Link
                    href="#estimation"
                    size="lg"
                    className="bg-orcsys-500 hover:bg-orcsys-600 text-white group"
                  >
                    Demandez un devis gratuit
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button.Link>
                </div>
              </motion.div>

              {/* Statistics */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.4 }}
                className="grid grid-cols-3 gap-4 md:gap-6 max-w-3xl mx-auto mb-6"
              >
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 text-center border border-slate-100 shadow-sm">
                  <div className="text-3xl font-bold text-orcsys-500 mb-1">98%</div>
                  <div className="text-slate-700 text-sm">Satisfaction clients</div>
                </div>
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 text-center border border-slate-100 shadow-sm">
                  <div className="text-3xl font-bold text-orcsys-500 mb-1">-40%</div>
                  <div className="text-slate-700 text-sm">Time-to-market</div>
                </div>
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 text-center border border-slate-100 shadow-sm">
                  <div className="flex items-start justify-center">
                    <div className="text-3xl font-bold text-orcsys-500 mb-1">24/7</div>
                    <div className="text-xl text-orcsys-500">*</div>
                  </div>
                  <div className="text-slate-700 text-sm">Support professionnel</div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.4 }} className="flex items-center justify-center mb-6">
                  <div className="text-orcsys-500 text-sm">* Support 24/7 garanti pour les incidents critiques</div>
              </motion.div>

              {/* Hero image */}
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="relative mx-auto"
              >
                <div className="relative rounded-xl overflow-hidden border border-slate-200 shadow-xl">
                  <div className="absolute inset-0 bg-gradient-to-tr from-orcsys-50/30 via-transparent to-orcsys-50/20 z-10" />
                  <img
                    src="https://images.unsplash.com/photo-1556155092-490a1ba16284?q=80&w=2070&auto=format&fit=crop"
                    alt="SaaS Development"
                    className="w-full h-auto object-cover"
                  />
                </div>

                {/* Floating elements */}
                <div className="absolute -top-6 -right-6 w-20 h-20">
                  <motion.div
                    className="w-full h-full bg-orcsys-500/10 rounded-xl backdrop-blur-sm border border-white/20 flex items-center justify-center"
                    animate={{
                      y: [0, -10, 0],
                      rotate: [0, 5, 0]
                    }}
                    transition={{
                      duration: 5,
                      repeat: Infinity,
                      repeatType: "reverse"
                    }}
                  >
                    <Code className="w-8 h-8 text-orcsys-500" />
                  </motion.div>
                </div>

                <div className="absolute -bottom-8 -left-8 w-24 h-24">
                  <motion.div
                    className="w-full h-full bg-orcsys-500/10 rounded-xl backdrop-blur-sm border border-white/20 flex items-center justify-center"
                    animate={{
                      y: [0, 10, 0],
                      rotate: [0, -5, 0]
                    }}
                    transition={{
                      duration: 6,
                      repeat: Infinity,
                      repeatType: "reverse"
                    }}
                  >
                    <Rocket className="w-10 h-10 text-orcsys-500" />
                  </motion.div>
                </div>
              </motion.div>

              {/* CTA - Schedule a call */}
              <motion.div
                className="mt-16 text-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.8 }}
              >
                <div className="inline-flex items-center text-slate-600 mb-2">
                  <Zap className="w-4 h-4 text-orcsys-500 mr-2" />
                  <span>Planifier un appel découverte de 30 minutes</span>
                </div>
                <div className="flex justify-center">
                  <a
                    href={`tel:${COMPANY_INFO.phone}`}
                    className="text-xl font-bold text-orcsys-500 hover:text-orcsys-600 transition-colors"
                  >
                    {COMPANY_INFO.phone}
                  </a>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Benefits section */}
        <section className="bg-white py-24">
          <div className="container px-4 mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Pourquoi choisir <span className="text-orcsys-500">Orcsys</span> pour votre SaaS?
              </h2>
              <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                Notre expertise en développement SaaS nous permet de créer des solutions performantes
                qui répondent parfaitement à vos besoins métier.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-lg border border-slate-100 hover:border-orcsys-100 transition-all duration-300 hover:shadow-xl"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true, margin: "-100px" }}
                >
                  <div className="w-14 h-14 rounded-lg bg-orcsys-50 flex items-center justify-center mb-6 p-3">
                    {benefit.icon}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{benefit.title}</h3>
                  <p className="text-slate-600">{benefit.description}</p>
                </motion.div>
              ))}
            </div>

            <motion.div
              className="mt-12 text-center"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <Button.Link
                href="#book"
                className="bg-orcsys-500 hover:bg-orcsys-600 text-white"
              >
                Discuter de votre projet
              </Button.Link>
            </motion.div>
          </div>
        </section>

        {/* Features section */}
        <section className="bg-slate-50 py-24">
          <div className="container px-4 mx-auto">
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-3xl font-bold text-slate-900 mb-6">
                    Des fonctionnalités <span className="text-orcsys-500">essentielles</span> pour votre SaaS
                  </h2>
                  <p className="text-lg text-slate-600 mb-8">
                    Nous intégrons les meilleures pratiques et fonctionnalités dans chaque SaaS pour
                    garantir performance, sécurité et scalabilité.
                  </p>

                  <ul className="space-y-4">
                    {[
                      "Architecture cloud-native pour une scalabilité maximale",
                      "Sécurité dès la conception avec authentification forte",
                      "Interfaces utilisateur conçues pour une expérience optimale",
                      "Intégrations API avec vos systèmes existants",
                      "Analyses et tableaux de bord personnalisés",
                      "Performances optimisées pour des temps de réponse rapides"
                    ].map((item, index) => (
                      <motion.li
                        key={index}
                        className="flex items-start"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                        viewport={{ once: true }}
                      >
                        <CheckCircle className="w-5 h-5 text-orcsys-500 mt-0.5 mr-3 flex-shrink-0" />
                        <span className="text-slate-700">{item}</span>
                      </motion.li>
                    ))}
                  </ul>

                  <div className="mt-8">
                    <Button.Link
                      href="#estimation"
                      variant="outline"
                      className="border-orcsys-400 text-orcsys-500"
                    >
                      Demander une estimation
                    </Button.Link>
                  </div>
                </div>

                <div>
                  <div className="relative p-1 bg-white rounded-2xl shadow-xl border border-slate-200">
                    <div className="absolute inset-0 bg-gradient-to-br from-orcsys-50/30 to-transparent rounded-2xl" />

                    <div className="relative grid grid-cols-2 gap-4 p-6">
                      {features.map((feature, index) => (
                        <motion.div
                          key={index}
                          className="group"
                          initial={{ opacity: 0, scale: 0.9 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.4, delay: index * 0.1 }}
                          viewport={{ once: true }}
                        >
                          <FunctionalityCard
                            icon={feature.icon}
                            title={feature.title}
                            description={feature.description}
                            index={index}
                          />
                        </motion.div>
                      ))}
                    </div>

                    {/* Blob backgrounds */}
                    <div className="absolute -top-10 -right-10 w-40 h-40 bg-orcsys-500/10 rounded-full blur-3xl" />
                    <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-orcsys-500/10 rounded-full blur-3xl" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Process section */}
        <section id="process" ref={processRef} className="bg-white py-24 relative overflow-hidden">
          <div className="container px-4 mx-auto relative z-10">
            <motion.div
              className="text-center mb-16"
              style={{ opacity: processOpacity }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Notre <span className="text-orcsys-500">processus</span> de développement
              </h2>
              <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                Une méthodologie éprouvée qui garantit des résultats de qualité, dans les délais
                et le budget convenus.
              </p>
            </motion.div>

            <div className="max-w-5xl mx-auto">
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-[15px] md:left-1/2 top-0 bottom-0 w-[2px] bg-slate-200 md:-ml-[1px]" />

                {/* Process steps */}
                {processSteps.map((step, index) => (
                  <motion.div
                    key={index}
                    className={`relative pl-10 md:pl-0 mb-12 ${index % 2 === 0 ? 'md:pr-[calc(50%+40px)]' : 'md:pl-[calc(50%+40px)]'
                      }`}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true, margin: "-100px" }}
                  >
                    {/* Timeline dot */}
                    <div className={`absolute left-0 md:left-1/2 top-0 w-8 h-8 bg-orcsys-50 border-4 border-white ring-2 ring-orcsys-500/20 rounded-full ${index % 2 === 0 ? 'md:-ml-4' : 'md:-ml-4'
                      }`}>
                      <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-orcsys-500">
                        {step.number.split('').map((char, i) => (
                          <span key={i}>{char}</span>
                        ))}
                      </span>
                    </div>

                    {/* Content */}
                    <div className={`bg-white p-6 rounded-xl shadow-lg border border-slate-100 hover:border-orcsys-100 transition-all duration-300 hover:shadow-xl ${index % 2 === 0 ? 'md:text-right' : ''
                      }`}>
                      <h3 className="text-xl font-bold text-slate-900 mb-2">{step.title}</h3>
                      <p className="text-slate-600">{step.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            <motion.div
              className="mt-12 text-center"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <Button.Link
                href="#book"
                className="bg-orcsys-500 hover:bg-orcsys-600 text-white"
              >
                Prendre rendez-vous
              </Button.Link>
            </motion.div>
          </div>

          {/* Background elements */}
          <div className="absolute top-1/4 left-0 w-96 h-96 bg-orcsys-500/5 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-orcsys-500/5 rounded-full blur-3xl" />
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-br from-orcsys-500 to-orcsys-600 py-20 relative overflow-hidden">
          {/* Background pattern */}
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBzdHJva2U9IiNmZmYiIHN0cm9rZS13aWR0aD0iMS41IiBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiIHN0cm9rZS1vcGFjaXR5PSIwLjEiPjxjaXJjbGUgY3g9IjMwIiBjeT0iMzAiIHI9IjE1Ii8+PHBhdGggZD0iTTMwIDE1TDQ1IDMwIDMwIDQ1IDE1IDMweiIvPjwvZz48L3N2Zz4=')] opacity-30" />

          <div className="container px-4 mx-auto relative z-10">
            <div className="max-w-4xl mx-auto text-center text-white">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Prêt à transformer votre idée en solution SaaS performante?
              </h2>
              <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
                Contactez-nous dès aujourd'hui pour discuter de votre projet et découvrir
                comment nous pouvons vous aider à le concrétiser rapidement.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button.Link
                  href="#estimation"
                  className="bg-white text-orcsys-600 hover:bg-white/90"
                  size="lg"
                >
                  Demander un devis gratuit
                </Button.Link>
                <Button.Link
                  href={`tel:${COMPANY_INFO.phone}`}
                  variant="outline"
                  className="border-white text-white hover:bg-white/10"
                  size="lg"
                >
                  Appeler maintenant
                </Button.Link>
              </div>
            </div>
          </div>
        </section>
        <EstimationForm />
        <BookingModule projectType="saas" />
        <FAQ
          title="Questions Fréquentes sur le Développement SaaS"
          subtitle="Découvrez notre expertise en développement de solutions SaaS"
          items={faqItems}
          className="bg-slate-50"
        />
      </div>
    </>
  );
};

export default SaasDevelopment;
